export class MemoryError extends Error {
  constructor(
    message: string,
    public code: string,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'MemoryError';
  }
}

export class AuthenticationError extends MemoryError {
  constructor(message = 'User authentication required', originalError?: unknown) {
    super(message, 'AUTHENTICATION_ERROR', originalError);
    this.name = 'AuthenticationError';
  }
}

export class ConfigurationError extends MemoryError {
  constructor(message: string, originalError?: unknown) {
    super(message, 'CONFIGURATION_ERROR', originalError);
    this.name = 'ConfigurationError';
  }
}

export class QdrantError extends MemoryError {
  constructor(message: string, originalError?: unknown) {
    super(message, 'QDRANT_ERROR', originalError);
    this.name = 'QdrantError';
  }
}

export class EmbeddingError extends MemoryError {
  constructor(message: string, originalError?: unknown) {
    super(message, 'EMBEDDING_ERROR', originalError);
    this.name = 'EmbeddingError';
  }
}

export class ValidationError extends MemoryError {
  constructor(message: string, originalError?: unknown) {
    super(message, 'VALIDATION_ERROR', originalError);
    this.name = 'ValidationError';
  }
}

// Para el reconocimiento de voz
export class SpeechRecognitionError extends MemoryError {
  constructor(message: string, originalError?: unknown) {
    super(message, 'SPEECH_RECOGNITION_ERROR', originalError);
    this.name = 'SpeechRecognitionError';
  }
}

// Para los errores de TTS
export class TTSError extends MemoryError {
  constructor(message: string, originalError?: unknown) {
    super(message, 'TTS_ERROR', originalError);
    this.name = 'TTSError';
  }
}

// Para los errores de completado de chat
export class ChatProcessingError extends MemoryError {
  constructor(message: string, originalError?: unknown) {
    super(message, 'CHAT_PROCESSING_ERROR', originalError);
    this.name = 'ChatProcessingError';
  }
}

// Para los errores de HTTP
export class HTTPError extends MemoryError {
  constructor(
    message: string, 
    public statusCode?: number,
    public response?: string,
    originalError?: unknown
  ) {
    super(message, 'HTTP_ERROR', originalError);
    this.name = 'HTTPError';
  }
}

// Para los errores de sesión
export class SessionError extends MemoryError {
  constructor(message: string, originalError?: unknown) {
    super(message, 'SESSION_ERROR', originalError);
    this.name = 'SessionError';
  }
}

// Para los errores de worker
export class WorkerError extends MemoryError {
  constructor(message: string, originalError?: unknown) {
    super(message, 'WORKER_ERROR', originalError);
    this.name = 'WorkerError';
  }
}

// Para los errores de eventos (alarmas, temporizadores, recordatorios)
export class EventError extends MemoryError {
  constructor(message: string, originalError?: unknown) {
    super(message, 'EVENT_ERROR', originalError);
    this.name = 'EventError';
  }
}

export function handleError(error: unknown, context: string): MemoryError {
  console.error(`[${context}] Error occurred:`, error);

  if (error instanceof MemoryError) {
    return error;
  }

  if (error instanceof Error) {
    // Manejar AbortError específicamente
    if (error.name === 'AbortError' || error.message.includes('aborted')) {
      return new ChatProcessingError('Operation was cancelled by user', error);
    }

    // Verificar patrones de error específicos
    if (error.message.includes('authentication') || error.message.includes('unauthorized')) {
      return new AuthenticationError(error.message, error);
    }

    if (error.message.includes('configuration') || error.message.includes('environment')) {
      return new ConfigurationError(error.message, error);
    }

    if (error.message.includes('Qdrant') || error.message.includes('HTTP 4') || error.message.includes('HTTP 5')) {
      return new QdrantError(error.message, error);
    }

    if (error.message.includes('embedding') || error.message.includes('Azure OpenAI')) {
      return new EmbeddingError(error.message, error);
    }

    // Errores de reconocimiento de voz y TTS
    if (error.message.includes('speech') || error.message.includes('recognition') || error.message.includes('microphone')) {
      return new SpeechRecognitionError(error.message, error);
    }

    if (error.message.includes('synthesis') || error.message.includes('TTS') || error.message.includes('audio') || error.message.includes('speaker')) {
      return new TTSError(error.message, error);
    }

    // Errores de procesamiento de chat
    if (error.message.includes('chat') || error.message.includes('completion') || error.message.includes('streaming')) {
      return new ChatProcessingError(error.message, error);
    }

    // Errores de sesión
    if (error.message.includes('session') || error.message.includes('token') || error.message.includes('cookie')) {
      return new SessionError(error.message, error);
    }

    // Errores de worker
    if (error.message.includes('worker') || error.message.includes('Worker')) {
      return new WorkerError(error.message, error);
    }
    
    // Errores de eventos
    if (error.message.includes('event') || error.message.includes('alarm') || 
        error.message.includes('timer') || error.message.includes('reminder')) {
      return new EventError(error.message, error);
    }

    return new MemoryError(error.message, 'UNKNOWN_ERROR', error);
  }

  return new MemoryError('An unknown error occurred', 'UNKNOWN_ERROR', error);
}

export function isRetryableError(error: MemoryError): boolean {
  // No reintentar errores de cliente (4xx) o problemas de autenticación
  if (error.message.includes('HTTP 4') || error.code === 'AUTHENTICATION_ERROR') {
    return false;
  }

  // No reintentar cancelaciones de usuario o errores de validación
  if (error.code === 'CHAT_PROCESSING_ERROR' && error.message.includes('cancelled')) {
    return false;
  }

  if (error.code === 'VALIDATION_ERROR') {
    return false;
  }

  // Reintentar estos tipos de errores
  return [
    'QDRANT_ERROR',
    'EMBEDDING_ERROR',
    'HTTP_ERROR',
    'TTS_ERROR',
    'SPEECH_RECOGNITION_ERROR',
    'WORKER_ERROR',
    'EVENT_ERROR'
  ].includes(error.code);
}

export async function withRetry<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000,
  context: string = 'Operation'
): Promise<T> {
  let lastError: MemoryError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = handleError(error, context);

      if (attempt === maxRetries || !isRetryableError(lastError)) {
        throw lastError;
      }

      console.warn(`[${context}] Attempt ${attempt} failed, retrying in ${delay}ms:`, lastError.message);
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }

  throw lastError!;
}


// Manejadores de errores especializados
export function handleSpeechError(error: unknown, operation: string): SpeechRecognitionError {
  if (error instanceof SpeechRecognitionError) {
    return error;
  }
  
  const context = `SpeechRecognition.${operation}`;
  const handledError = handleError(error, context);
  
  if (handledError instanceof SpeechRecognitionError) {
    return handledError;
  }
  
  return new SpeechRecognitionError(handledError.message, error);
}

export function handleTTSError(error: unknown, operation: string): TTSError {
  if (error instanceof TTSError) {
    return error;
  }
  
  const context = `TTS.${operation}`;
  const handledError = handleError(error, context);
  
  if (handledError instanceof TTSError) {
    return handledError;
  }
  
  return new TTSError(handledError.message, error);
}

export function handleChatError(error: unknown, operation: string): ChatProcessingError {
  if (error instanceof ChatProcessingError) {
    return error;
  }
  
  const context = `Chat.${operation}`;
  const handledError = handleError(error, context);
  
  if (handledError instanceof ChatProcessingError) {
    return handledError;
  }
  
  return new ChatProcessingError(handledError.message, error);
}

export function handleHTTPError(
  response: Response, 
  context: string, 
  responseText?: string
): HTTPError {
  const message = `HTTP ${response.status} ${response.statusText}${responseText ? `: ${responseText}` : ''}`;
  return new HTTPError(message, response.status, responseText);
}

export function handleSessionError(error: unknown, operation: string): SessionError {
  if (error instanceof SessionError) {
    return error;
  }
  
  const context = `Session.${operation}`;
  const handledError = handleError(error, context);
  
  if (handledError instanceof SessionError) {
    return handledError;
  }
  
  return new SessionError(handledError.message, error);
}

export function handleEventError(error: unknown, operation: string): EventError {
  if (error instanceof EventError) {
    return error;
  }
  
  const context = `Event.${operation}`;
  const handledError = handleError(error, context);
  
  if (handledError instanceof EventError) {
    return handledError;
  }
  
  return new EventError(handledError.message, error);
}

// Clase de Logger centralizada
export class Logger {
  private static formatMessage(level: string, context: string, message: string): string {
    const timestamp = new Date().toISOString();
    return `%c[${timestamp}] [${level.toUpperCase()}] [${context}] ${message}`;
  }

  private static getLogStyle(level: string): string {
    const styles = {
      error: 'color: #f44336; font-weight: bold;',
      warn: 'color: #ff9800; font-weight: bold;',
      info: 'color: #2196f3; font-weight: bold;',
      debug: 'color: #4caf50; font-weight: bold;',
      speech: 'color: #00bcd4; font-weight: bold;',
      tts: 'color: #4caf50; font-weight: bold;',
      chat: 'color: #9c27b0; font-weight: bold;',
      memory: 'color: #607d8b; font-weight: bold;'
    };
    return styles[level as keyof typeof styles] || styles.info;
  }

  static error(context: string, message: string, ...args: any[]): void {
    console.error(this.formatMessage('error', context, message), this.getLogStyle('error'), ...args);
  }

  static warn(context: string, message: string, ...args: any[]): void {
    console.warn(this.formatMessage('warn', context, message), this.getLogStyle('warn'), ...args);
  }

  static info(context: string, message: string, ...args: any[]): void {
    console.log(this.formatMessage('info', context, message), this.getLogStyle('info'), ...args);
  }

  static debug(context: string, message: string, ...args: any[]): void {
    console.log(this.formatMessage('debug', context, message), this.getLogStyle('debug'), ...args);
  }

  static speech(message: string, ...args: any[]): void {
    console.log(this.formatMessage('speech', 'SPEECH-REC', message), this.getLogStyle('speech'), ...args);
  }

  static tts(message: string, ...args: any[]): void {
    console.log(this.formatMessage('tts', 'AZURE-TTS', message), this.getLogStyle('tts'), ...args);
  }

  static chat(message: string, ...args: any[]): void {
    console.log(this.formatMessage('chat', 'CHAT-MGR', message), this.getLogStyle('chat'), ...args);
  }

  static memory(message: string, ...args: any[]): void {
    console.log(this.formatMessage('memory', 'MEMORY-SVC', message), this.getLogStyle('memory'), ...args);
  }
}